import * as React from 'react'
import * as tocStyles from './article-toc.module.scss'
import ContactSupport from '../contact-support/contact-support';
import { useSiteMetadata } from '../../queries/site-medatata';
import { usePageData } from '../../hooks/page-data';
import { sortByDate } from '../../utilities/sort-by-date';
import pluralize from 'pluralize';
import unescapeAttribute from '../../utilities/unescape-attribute';
import { getScrollPadding } from '../../utilities/get-scroll-padding';

const scrollParentTo = (id) => {
    if (!id) {
        return;
    }

    const element = document.getElementById(id);

    if (!element || !element.offsetParent || element.offsetParent.scrollHeight <= element.offsetParent.offsetHeight) {
        return;
    }

    const pos = element.offsetTop - element.offsetParent.offsetHeight / 2;

    element.offsetParent.scrollTo({ top: pos });
}

const ArticleToc = () => {
    const data = usePageData();

    const contributeRelativeUrl = React.useMemo(() => {
        if (data?.markdownRemark?.fields?.relativeUrl) {
            return `${data?.markdownRemark.fields.relativeUrl}.md`;
        }

        if (data?.kendoApiType?.parent?.relativePath) {
            return `/api/${data.kendoApiType.parent.relativePath}`;
        }

        if (data?.kendoApiMember?.kendoApiType?.parent?.relativePath) {
            return `/api/${data.kendoApiMember.kendoApiType.parent.relativePath}`;
        }

        return null;
    }, [data]);

    const headings = React.useMemo(() => {
        const result = [];

        if (data?.markdownRemark) {
            if (!data?.markdownRemark?.htmlAst?.children) {
                return result;
            }

            for (const node of data.markdownRemark.htmlAst.children) {
                const match = node.tagName?.match(/^h(?<level>\d$)/i)
                if (!match || !node.properties[`dataToc`]) {
                    continue;
                }

                result.push({
                    id: node.properties.id,
                    text: unescapeAttribute(node.properties[`dataToc`]),
                    level: parseInt(match.groups.level, 10)
                });
            }
        }

        if (data?.release && data?.latestDevRelease) {
            if (!data?.release?.nodes && data?.latestDevRelease.nodes) {
                return result;
            }

            for (const node of [...data?.latestDevRelease.nodes, ...data.release.nodes].sort(sortByDate)) {
                result.push({
                    id: node.name,
                    text: node.name,
                    level: 2
                });
            }
        }

        if (data?.apiType) {
            if (data.apiType.definition) {
                result.push({
                    id: `definition`,
                    text: `Definition`,
                    level: 2
                });
            }

            const byNameInsensitive = (a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });

            const groups = (data.apiType.members || []).reduce((acc, m) => {
                acc[m.typeName] = [
                    ...(acc[m.typeName] || []),
                    m
                ];
                return acc;
            }, {});

            const orderedGroups = Object.entries(groups)
                .map(([groupName, members]) => ({
                    groupName,
                    members: [...members].sort(byNameInsensitive),
                    position: Math.min(...members.map(m => m.position ?? 999))
                }))
                .sort((a, b) => a.position - b.position);

            orderedGroups.forEach(({ groupName, members }) => {
                const isSelf = members.length === 1 && members[0].typeName === members[0].name;

                if (!isSelf) {
                    result.push({
                        id: pluralize(groupName).toLowerCase(),
                        text: pluralize(groupName),
                        level: 2
                    });

                    members.forEach(m => {
                        result.push({
                            id: m.hash,
                            text: m.name,
                            level: 3
                        });
                    });
                }
            });

            if (data.apiType.extensionMethods?.length > 0) {
                result.push({
                    id: `extension-methods`,
                    text: `Extension Methods`,
                    level: 2
                });
            }
        }

        if (data?.allKendoApiMember?.group) {
            data.allKendoApiMember.group.forEach(g => {
                result.push({
                    id: g.name.replaceAll(/[^a-z0-9]+/gi, '-').toLowerCase(),
                    text: g.name,
                    level: 2
                });
            });
        }

        if (data?.kendoApiMember) {
            [data.kendoApiMember, ...(data.childMembers.nodes)].forEach(m => {
                result.push({
                    id: m.name.replaceAll('.', '').toLowerCase(),
                    text: m.name,
                    level: 3
                });
            });

            if (data.kendoApiMember.relatedMembers.length > 0) {
                result.push({
                    id: `related-properties`,
                    text: `Related Properties`,
                    level: 3
                });
            }
        }

        return result;
    }, [data]);

    const tocItems = React.useMemo(() => {
        const result = headings
            .filter(h => h.level === 2 || h.level === 3);

        return result;
    }, [headings]);

    const [activeId, setActiveId] = React.useState(null);

    const timerRef = React.useRef(null);

    const articleRef = React.useRef(null);

    React.useEffect(() => {
        const handler = () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }

            let newId = null;
            let scrollY = Math.ceil(window.scrollY + getScrollPadding());

            for (let item of tocItems) {
                const el = document.getElementById(item.id);

                if (!el) {
                    continue;
                }

                if (el.offsetTop > scrollY + 1) {
                    break;
                }

                newId = el?.id;
            }

            setActiveId(newId);

            timerRef.current = setTimeout(() => {
                if (newId) {
                    scrollParentTo(`toc-${newId}`);
                } else {
                    articleRef?.current?.scrollTo({ top: 0 });
                }
            }, 100);
        };

        window.addEventListener(`scroll`, handler, { passive: true });
        handler();

        return () => window.removeEventListener(`scroll`, handler);
    }, [tocItems]);

    const siteMetadata = useSiteMetadata();

    return (<>
        {tocItems.length > 0 && <div className={tocStyles.articleOutline}>
            <div className={tocStyles.tocHeader}>In this article</div>
            <div ref={articleRef} className={"dark-scrollbar " + tocStyles.tocWrap} style={{ maxHeight: `calc(100vh - 398px)` }}>
                <div className={tocStyles.verticalIndicator}>
                    {tocItems.map(i => (<a key={i.id} id={`toc-${i.id}`} className={`${tocStyles.tocLink} ${i.id === activeId ? tocStyles.active : ''} ${i.level === 3 ? tocStyles.secondary : ''}`} href={`#${i.id}`}>{i.text}</a>))}
                </div>
            </div>
        </div>}
        <div className={tocStyles.contactWrap}>
            <ContactSupport />
            {siteMetadata.contributeUrl && contributeRelativeUrl && <>
                <div>
                    <a className={tocStyles.contributeLink} href={`${siteMetadata.contributeUrl}${contributeRelativeUrl}`} target='_blank' rel="noreferrer">
                        <svg viewBox="0 0 16 17" className={`docs-icon ${tocStyles.editIcon}`} xmlns="http://www.w3.org/2000/svg">
                            <path d="M13 10.2002V13.2002H3V3.2002H6V4.2002H4V12.2002H12V10.2002H13ZM13 3.2002H8L9.77812 4.9752L9.79375 4.99082L6 8.78457L7.41563 10.2002L11.2063 6.40645L13 8.2002V3.2002Z" />
                        </svg>
                        <span>Improve this article</span>
                    </a>
                </div>
            </>}
        </div>
    </>)
}

export default ArticleToc;